/**
 * Browser Sentry initialization for Xenomons.
 *
 * Guarded: only runs in the browser and only when a DSN is present. On dev
 * (no DSN configured) this is a no-op, so nothing crashes locally.
 *
 * Imported once at the very top of src/router.tsx.
 */
import * as Sentry from "@sentry/react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DSN = (import.meta.env as any)?.VITE_SENTRY_DSN as string | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RELEASE = (import.meta.env as any)?.VITE_SENTRY_RELEASE as string | undefined;

let initialized = false;

export function initSentryClient(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!DSN) return;
  initialized = true;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    release: RELEASE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    beforeSend(event, hint) {
      const err = hint?.originalException;
      const msg =
        (err instanceof Error ? err.message : typeof err === "string" ? err : event.message) ?? "";
      // Grundrauschen filtern
      if (/ResizeObserver loop/i.test(msg)) return null;
      if (/AbortError|The operation was aborted/i.test(msg)) return null;
      if (/NotAllowedError/i.test(msg)) return null; // Audio / Vibration ohne User-Geste
      if (/Failed to fetch dynamically imported module/i.test(msg)) return null; // Deploys mid-session
      return event;
    },
  });
}

export function setSentryUser(userId: string | null): void {
  if (!initialized) return;
  Sentry.setUser(userId ? { id: userId } : null);
}

export function captureClientException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;
  Sentry.captureException(error, context ? { contexts: { lovable: context } } : undefined);
}

// Auto-init on module load (browser only, no-op on SSR).
initSentryClient();
